import replaceFrenchAccents from 'utils/replace-french-accents';
import { SortBy, TopCitiesResponse } from '@zoocasa/go-search';
import { countryCodeFromProvinceOrState, ProvinceAndState } from 'utils/province_or_state';
import { cleanDashedString } from 'data/addresses';
import { capitalizeWords } from '@zoocasa/node-kit/strings';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import defaultListingParams from 'contexts/preferences/listing-params/defaults';
import { SearchApi, SearchApiType } from 'data/search/api';
import { getGoSearchHost, getInsightsHost, isServerSide } from 'utils/host-config';
import { cloneDeep } from 'lodash';
import deepmerge from 'deepmerge';
import { DEFAULT_HAS_IMAGE_VALUE } from 'contexts/preferences/listing-params';
import { SearchByAreaFilterType } from 'data/search/area/types';
import { getSurroundingCities } from 'utils/related-searches';
import { TenantTypes } from '@tenants';
import { standardizedCityName } from 'utils/standardized-city-name';
import type { LinkDataType } from 'components/home-page/internal-links';
import { LOCALE_INTERNAL_LINKS_PATHS, type internalLinksPaths } from 'types/locales';

export interface NearbyCity {
  city: string;
  province: string;
  slug: string;
}
export interface NearbyNeighbourhood {
  neighbourhood: string;
  city: string;
  province: string;
  slug: string;
}

/**
 * Localized link data structure that supports translation keys and interpolation.
 * This type is used by the new localized footer generation functions.
 */
export type LocalizedLinkData = {
  /** Translation key from LOCALE_INTERNAL_LINKS_PATHS */
  titleKey?: internalLinksPaths;
  /** Parameters for interpolation in the title (e.g., { area: "Toronto" }) */
  titleParams?: Record<string, string>;
  /** Fallback title string for backward compatibility */
  title?: string;
  links: {
    /** Translation key from LOCALE_INTERNAL_LINKS_PATHS */
    labelKey?: internalLinksPaths;
    /** Parameters for interpolation in the label (e.g., { city: "Toronto" }) */
    labelParams?: Record<string, string>;
    /** Fallback label string for backward compatibility */
    label?: string;
    /** URL link */
    link: string;
  }[];
};


const RECENT_LISTINGS_IDX = 0;
const PROPERTY_TYPES_IDX = 1;
const NEARBY_NEIGHBOURHOODS_IDX = 2;
export const NEARBY_CITIES_IDX = 3;
const HOUSES_FOR_SALE_IDX = 4;
const CONDOS_FOR_SALE_IDX = 5;
const HOMES_FOR_RENT_IDX = 6;
const RECENTLY_SOLD_IDX = 7;

function createLinkData(title: string):LinkDataType {
  return {
    title,
    links: [],
  };
}

function createFooterDataTemplate(area = ''): LinkDataType[] {
  const footerDataTemplate: LinkDataType[] = new Array(7);
  const hasAreaStr = area.trim().length > 0;
  const prefix = hasAreaStr ? `${area} ` : '';
  footerDataTemplate[RECENT_LISTINGS_IDX] = createLinkData(`${prefix}Latest Listings`);
  footerDataTemplate[PROPERTY_TYPES_IDX] = createLinkData(`${hasAreaStr ? prefix : 'Check by'} Property Types`);
  footerDataTemplate[NEARBY_NEIGHBOURHOODS_IDX] = createLinkData(`${prefix}Neighbourhoods`);
  footerDataTemplate[NEARBY_CITIES_IDX] = createLinkData(`${hasAreaStr ? 'Popular ' : ''}Nearby Cities`);
  footerDataTemplate[HOUSES_FOR_SALE_IDX] = createLinkData(`Houses for Sale near ${area}`);
  footerDataTemplate[CONDOS_FOR_SALE_IDX] = createLinkData(`Condos for Sale near ${area}`);
  footerDataTemplate[HOMES_FOR_RENT_IDX] = createLinkData(`For Rent near ${area}`);
  footerDataTemplate[RECENTLY_SOLD_IDX] = createLinkData(`Recently sold near ${area}`);
  return footerDataTemplate;
}

function createPropertyTypesLinkData(location: { area: string; slug: string }) {
  const formattedSlug = replaceFrenchAccents(location.slug);
  const propertyTypesLinks = [
    {
      label: `Houses for Sale ${location.area}`,
      link: `/${formattedSlug}-real-estate/houses`,
    }, {
      label: `Condos for Sale ${location.area}`,
      link: `/${formattedSlug}-real-estate/condos`,
    }, {
      label: `Townhouses for Sale ${location.area}`,
      link: `/${formattedSlug}-real-estate/townhouses`,
    }, {
      label: `For Rent near ${location.area}`,
      link: `/${formattedSlug}-real-estate/for-rent`,
    },
  ];
  return propertyTypesLinks;
}

//FIXME: This function uses the tenant.theme.id as the tenant ID. We need to use the tenant.id instead.
async function getRecentListings(province: string, city: string, neighbourhood: string, searchApi: SearchApiType, tenant: TenantTypes) {
  const country = countryCodeFromProvinceOrState(province);
  const filter: SearchByAreaFilterType = cloneDeep(deepmerge(defaultListingParams.filter, { hasImage: DEFAULT_HAS_IMAGE_VALUE, homeType: { house: true, condo: true, townhouse: true, land: true, commercial: true, farm: true }, tenant: tenant.theme.id }));
  const listings = await searchApi.searchByArea(country, province, city, neighbourhood, filter, tenant.theme.id, SortBy.DateDesc, 0, 10);

  const recentListings = listings.data
    .map(listing => {
      const { streetName, streetNumber, addressUrlAbsolutePath, listingUrlAbsolutePath } = listing;
      const label = streetNumber ? `${streetNumber} ${streetName}` : streetName;
      return {
        label: capitalizeWords(label.toLowerCase()),
        link: cleanDashedString(addressUrlAbsolutePath || listingUrlAbsolutePath),
      };
    });
  return recentListings;
}

export async function getPopularNeighbourhoods(city: string, city_slug: string, province: string) {
  const country = countryCodeFromProvinceOrState(province);
  let resp;
  const popularNearbyNeighbourhoodsUrl = `${getInsightsHost(isServerSide())}/insights/popular/${country}/${province}/${city}?limit=10`;
  try {
    const response = await fetchWithRetry(popularNearbyNeighbourhoodsUrl, { method: 'GET' });

    if (!response.ok) {
      console.error('Failed to fetch popular nearby neighbourhoods: %d %s', response.status, response.statusText);
      return [];
    }
    const content = await response.blob();
    const buffer = await content.arrayBuffer();
    const topCities = TopCitiesResponse.decode(new Uint8Array(buffer)).topCities;
    resp = await Promise.all(topCities.slice(0, 10).map(async c => {
      return {
        label: c.Name,
        link: `/${city_slug}-real-estate/${c.Slug.replace(`-${city_slug}`, '')}`,
      };
    }));
    return resp;
  } catch (error: unknown) {
    console.error(`Failed to fetch popular nearby neighbourhoods: ${error}`);
    return [];
  }
}

async function getNearbyNeighbourhoods(city_slug: string, latitude: number, longitude: number) {
  if (latitude && longitude) {
    let resp;
    const nearbyNeighbourhoodsUrl = `${getInsightsHost(isServerSide())}/insights/nearby?latitude=${latitude}&longitude=${longitude}`;
    try {
      const response = await fetchWithRetry(nearbyNeighbourhoodsUrl, { method: 'GET' });

      if (!response.ok) {
        console.error('Failed to fetch nearby neighbourhoods: %d %s', response.status, response.statusText);
        return [];
      }
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const topCities = TopCitiesResponse.decode(new Uint8Array(buffer)).topCities;
      resp = await Promise.all(topCities.slice(0, 10).map(async c => {
        return {
          label: c.Name,
          link: `${city_slug}-real-estate/${c.Slug.replace(`-${city_slug}`, '')}`,
        };
      }));
      return resp;
    } catch (error: unknown) {
      console.error(`Failed to fetch popular nearby cities: ${error}`);
      return [];
    }
  }
  return [];
}

//FIXME: This function uses the tenant.theme.id as the tenant ID. We need to use the tenant.id instead.
export default async function generateCityFooterData(city: string, city_slug: string, tenant: TenantTypes, province: keyof typeof ProvinceAndState, limit = 10, useLegacySearchFilter = true, isSold: boolean): Promise<LinkDataType[]>{
  // 1) Build the default template
  const nearbyCityMap = await getSurroundingCities(ProvinceAndState[province]) || [];
  const footerDataTemplate: LinkDataType[] = createFooterDataTemplate(city);
  const searchApi = SearchApi.create(getGoSearchHost(isServerSide()), useLegacySearchFilter);

  // 2) Populate the usual links (only used if isSold is false, or partially used if true)
  footerDataTemplate[PROPERTY_TYPES_IDX].links = createPropertyTypesLinkData({ area: city, slug: city_slug });
  footerDataTemplate[RECENT_LISTINGS_IDX].links = await getRecentListings(province, city, '', searchApi, tenant);

  // 3) Fill in data for "Houses for Sale," "For Rent," etc.
  nearbyCityMap.slice(0, limit).forEach((currentValue: NearbyCity) => {
    const formattedSlug = replaceFrenchAccents(currentValue.slug);
    const formattedCityName = standardizedCityName(currentValue.city);

    // Houses for Sale
    footerDataTemplate[HOUSES_FOR_SALE_IDX].links.push({
      label: `${formattedCityName} Houses for Sale`,
      // SEO: internal links with houses as anchor text should link to the base level area pages instead of the houses subvariant
      link: `/${formattedSlug}-real-estate`,
    });

    // For Rent near ...
    footerDataTemplate[HOMES_FOR_RENT_IDX].links.push({
      label: `${formattedCityName} Houses for Rent`,
      link: `/${formattedSlug}-real-estate/for-rent`,
    });

    // Condos for Sale
    footerDataTemplate[CONDOS_FOR_SALE_IDX].links.push({
      label: `${formattedCityName} Condos For Sale`,
      link: `/${formattedSlug}-real-estate/condos`,
    });

    // "Popular Nearby Cities" (Homes for Sale)
    footerDataTemplate[NEARBY_CITIES_IDX].links.push({
      label: `${formattedCityName} Homes for Sale`,
      link: `/${formattedSlug}-real-estate`,
    });

    // "Recently sold"
    footerDataTemplate[RECENTLY_SOLD_IDX].links.push({
      label: `Recently Sold Homes in ${formattedCityName}`,
      link: `/${formattedSlug}-real-estate/sold`,
    });
  });

  // 4) Add popular neighbourhoods (only if not sold)
  const popularNeighbourhoods = await getPopularNeighbourhoods(city, city_slug, province);
  if (!isSold) {
    // Only keep the "Nearby Neighbourhoods" section in the normal (non-sold) scenario
    if (popularNeighbourhoods?.length > 0) {
      footerDataTemplate[NEARBY_NEIGHBOURHOODS_IDX].links = popularNeighbourhoods.map((item: { label: string; link: string }) => {
        return {
          ...item,
          label: capitalizeWords(item.label),
        };
      });
    } else {
      footerDataTemplate.splice(NEARBY_NEIGHBOURHOODS_IDX, 1);
    }
  }

  // 5) If isSold is false, return the full standard data
  if (!isSold) {
    return footerDataTemplate;
  }

  // ------------------------------------------------------------------
  // If isSold = true, we override the standard sections and build
  // the final data array with only:
  //   a) "Active Listing in Nearby Cities" (replace “Popular Nearby Cities”)
  //   b) "Active Rentals near [city]"      (replace “For Rent near [city]”)
  //   c) NEW "Recently sold near [city]"
  // ------------------------------------------------------------------

  // 5a) Rename the two sections we want to keep
  footerDataTemplate[NEARBY_CITIES_IDX].title = 'Active Listing in Nearby Cities';
  footerDataTemplate[HOMES_FOR_RENT_IDX].title = `Active Rentals near ${city}`;

  // 5b) Keep only those two
  const finalFooterData: LinkDataType[] = [
    footerDataTemplate[NEARBY_CITIES_IDX], // index 3
    footerDataTemplate[HOMES_FOR_RENT_IDX], // index 6
  ];

  // 5c) Create a NEW "Recently sold near [city]" section
  const recentlySoldSection = createLinkData(`Recently sold near ${city}`);

  nearbyCityMap.slice(0, 10).forEach((currentValue: NearbyCity) => {
    const formattedSlug = replaceFrenchAccents(currentValue.slug);
    const formattedCityName = standardizedCityName(currentValue.city);

    recentlySoldSection.links.push({
      label: `Recently Sold Homes in ${formattedCityName}`,
      link: `/${formattedSlug}-real-estate/sold`,
    });
  });

  finalFooterData.push(recentlySoldSection);

  return finalFooterData;
}

//FIXME: This function uses the tenant.theme.id as the tenant ID. We need to use the tenant.id instead.
export async function generateNeighbourhoodFooterData(city: string, city_slug: string, neighbourhood: string, neighbourhood_slug: string, province: keyof typeof ProvinceAndState, latitude: number, longitude: number, tenant: TenantTypes, useLegacySearchFilter = true): Promise<LinkDataType[]>{
  const nearbyCityMap = await getSurroundingCities(ProvinceAndState[province]) || [];
  const footerDataTemplate: LinkDataType[] = createFooterDataTemplate(city);
  const searchApi = SearchApi.create(getGoSearchHost(isServerSide()), useLegacySearchFilter);
  footerDataTemplate[PROPERTY_TYPES_IDX].links = createPropertyTypesLinkData({ area: neighbourhood, slug: neighbourhood_slug });
  footerDataTemplate[RECENT_LISTINGS_IDX].links = await getRecentListings(province, city, neighbourhood, searchApi, tenant);

  const footerData = nearbyCityMap.reduce((previousValue: LinkDataType[], currentValue: NearbyCity)=> {
    const formattedCityName = standardizedCityName(currentValue.city);
    previousValue?.[HOUSES_FOR_SALE_IDX]?.links.push({
      label: `${formattedCityName} Houses for Sale`,
      link: `/${currentValue.slug}-real-estate/houses`,
    });
    previousValue?.[HOMES_FOR_RENT_IDX]?.links.push({
      label: `${formattedCityName} Houses for Rent`,
      link: `/${currentValue.slug}-real-estate/filter?rental=true&townhouse=false&condo=false`,
    });
    previousValue?.[CONDOS_FOR_SALE_IDX]?.links.push({
      label: `${formattedCityName} Condos For Sale`,
      link: `/${currentValue.slug}-real-estate/condos`,
    });
    previousValue?.[NEARBY_CITIES_IDX]?.links.push({
      label: `${formattedCityName} Homes for Sale`,
      link: `/${currentValue.slug}-real-estate`,
    });
    return previousValue;
  }, footerDataTemplate);

  // TODO: replace with go-search func & remove service
  const nearbyNeighbourhood = await getNearbyNeighbourhoods(city_slug, latitude, longitude);
  if (nearbyNeighbourhood?.length > 0) {
    footerDataTemplate[NEARBY_NEIGHBOURHOODS_IDX].links = nearbyNeighbourhood;
  } else {
    footerDataTemplate.splice(NEARBY_NEIGHBOURHOODS_IDX, 1);
  }

  return footerData;
}

// ============================================================================
// LOCALIZED VARIANTS - Translation-ready footer generation functions
// ============================================================================

/**
 * Creates a localized link data object with translation key and parameters.
 * @param titleKey - Translation key from LOCALE_INTERNAL_LINKS_PATHS
 * @param titleParams - Optional parameters for interpolation
 * @param fallbackTitle - Fallback title for default value
 */
function createLocalizedLinkData(
  titleKey: internalLinksPaths,
  titleParams?: Record<string, string>,
  fallbackTitle?: string
): LocalizedLinkData {
  const result: LocalizedLinkData = {
    titleKey,
    title: fallbackTitle,
    links: [],
  };
  
  // Only add titleParams if it's defined (Next.js serialization requirement)
  if (titleParams) {
    result.titleParams = titleParams;
  }
  
  return result;
}

/**
 * Creates a localized footer data template with translation keys.
 * This is the localized version of createFooterDataTemplate().
 */
function createLocalizedFooterDataTemplate(area = ''): LocalizedLinkData[] {
  const footerDataTemplate: LocalizedLinkData[] = new Array(7);
  const hasAreaStr = area.trim().length > 0;
  
  footerDataTemplate[RECENT_LISTINGS_IDX] = hasAreaStr
    ? createLocalizedLinkData(
      LOCALE_INTERNAL_LINKS_PATHS.FOOTER_LATEST_LISTINGS_WITH_AREA,
      { area },
      `${area} Latest Listings`
    )
    : createLocalizedLinkData(
      LOCALE_INTERNAL_LINKS_PATHS.FOOTER_LATEST_LISTINGS,
      undefined,
      'Latest Listings'
    );
  
  footerDataTemplate[PROPERTY_TYPES_IDX] = hasAreaStr
    ? createLocalizedLinkData(
      LOCALE_INTERNAL_LINKS_PATHS.FOOTER_PROPERTY_TYPES_WITH_AREA,
      { area },
      `${area} Property Types`
    )
    : createLocalizedLinkData(
      LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CHECK_BY_PROPERTY_TYPES,
      undefined,
      'Check by Property Types'
    );
  
  footerDataTemplate[NEARBY_NEIGHBOURHOODS_IDX] = hasAreaStr
    ? createLocalizedLinkData(
      LOCALE_INTERNAL_LINKS_PATHS.FOOTER_NEIGHBOURHOODS_WITH_AREA,
      { area },
      `${area} Neighbourhoods`
    )
    : createLocalizedLinkData(
      LOCALE_INTERNAL_LINKS_PATHS.FOOTER_NEIGHBOURHOODS,
      undefined,
      'Neighbourhoods'
    );
  
  footerDataTemplate[NEARBY_CITIES_IDX] = createLocalizedLinkData(
    hasAreaStr
      ? LOCALE_INTERNAL_LINKS_PATHS.FOOTER_POPULAR_NEARBY_CITIES
      : LOCALE_INTERNAL_LINKS_PATHS.FOOTER_NEARBY_CITIES,
    undefined,
    hasAreaStr ? 'Popular Nearby Cities' : 'Nearby Cities'
  );
  
  footerDataTemplate[HOUSES_FOR_SALE_IDX] = createLocalizedLinkData(
    LOCALE_INTERNAL_LINKS_PATHS.FOOTER_HOUSES_FOR_SALE_NEAR,
    { area },
    `Houses for Sale near ${area}`
  );
  
  footerDataTemplate[CONDOS_FOR_SALE_IDX] = createLocalizedLinkData(
    LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CONDOS_FOR_SALE_NEAR,
    { area },
    `Condos for Sale near ${area}`
  );
  
  footerDataTemplate[HOMES_FOR_RENT_IDX] = createLocalizedLinkData(
    LOCALE_INTERNAL_LINKS_PATHS.FOOTER_FOR_RENT_NEAR,
    { area },
    `For Rent near ${area}`
  );
  
  footerDataTemplate[RECENTLY_SOLD_IDX] = createLocalizedLinkData(
    LOCALE_INTERNAL_LINKS_PATHS.FOOTER_RECENTLY_SOLD_NEAR,
    { area },
    `Recently sold near ${area}`
  );
  
  return footerDataTemplate;
}

/**
 * Creates localized property types link data with translation keys.
 * This is the localized version of createPropertyTypesLinkData().
 */
function createLocalizedPropertyTypesLinkData(location: { area: string; slug: string }) {
  const formattedSlug = replaceFrenchAccents(location.slug);
  const propertyTypesLinks = [
    {
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_HOUSES_FOR_SALE_IN_AREA,
      labelParams: { area: location.area },
      label: `Houses for Sale ${location.area}`,
      link: `/${formattedSlug}-real-estate/houses`,
    }, {
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CONDOS_FOR_SALE_IN_AREA,
      labelParams: { area: location.area },
      label: `Condos for Sale ${location.area}`,
      link: `/${formattedSlug}-real-estate/condos`,
    }, {
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_TOWNHOUSES_FOR_SALE_IN_AREA,
      labelParams: { area: location.area },
      label: `Townhouses for Sale ${location.area}`,
      link: `/${formattedSlug}-real-estate/townhouses`,
    }, {
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_FOR_RENT_NEAR_AREA,
      labelParams: { area: location.area },
      label: `For Rent near ${location.area}`,
      link: `/${formattedSlug}-real-estate/for-rent`,
    },
  ];
  return propertyTypesLinks;
}

/**
 * Generates localized city footer data with translation keys.
 * This is the localized version of generateCityFooterData() that returns
 * LocalizedLinkData with translation keys instead of hardcoded strings.
 * 
 * @param city - City name (e.g., "Toronto")
 * @param city_slug - City slug for URLs (e.g., "toronto-on")
 * @param tenant - Tenant configuration
 * @param province - Province/state key
 * @param limit - Maximum number of nearby cities to include
 * @param useLegacySearchFilter - Whether to use legacy search filter
 * @param isSold - Whether this is for sold listings page
 * @returns Promise<LocalizedLinkData[]> - Array of localized link data with translation keys
 */
//FIXME: This function uses the tenant.theme.id as the tenant ID. We need to use the tenant.id instead.
export async function generateLocalizedCityFooterData(
  city: string,
  city_slug: string,
  tenant: TenantTypes,
  province: keyof typeof ProvinceAndState,
  limit = 10,
  useLegacySearchFilter = true,
  isSold: boolean
): Promise<LocalizedLinkData[]> {
  // 1) Build the default template with translation keys
  const nearbyCityMap = await getSurroundingCities(ProvinceAndState[province]) || [];
  const footerDataTemplate: LocalizedLinkData[] = createLocalizedFooterDataTemplate(city);
  const searchApi = SearchApi.create(getGoSearchHost(isServerSide()), useLegacySearchFilter);

  // 2) Populate the usual links (only used if isSold is false, or partially used if true)
  footerDataTemplate[PROPERTY_TYPES_IDX].links = createLocalizedPropertyTypesLinkData({ area: city, slug: city_slug });
  footerDataTemplate[RECENT_LISTINGS_IDX].links = await getRecentListings(province, city, '', searchApi, tenant);

  // 3) Fill in data for "Houses for Sale," "For Rent," etc. with translation keys
  nearbyCityMap.slice(0, limit).forEach((currentValue: NearbyCity) => {
    const formattedSlug = replaceFrenchAccents(currentValue.slug);
    const formattedCityName = standardizedCityName(currentValue.city);

    // Houses for Sale
    footerDataTemplate[HOUSES_FOR_SALE_IDX].links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_HOUSES_FOR_SALE,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Houses for Sale`,
      // SEO: internal links with houses as anchor text should link to the base level area pages instead of the houses subvariant
      link: `/${formattedSlug}-real-estate`,
    });

    // For Rent near ...
    footerDataTemplate[HOMES_FOR_RENT_IDX].links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_HOUSES_FOR_RENT,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Houses for Rent`,
      link: `/${formattedSlug}-real-estate/for-rent`,
    });

    // Condos for Sale
    footerDataTemplate[CONDOS_FOR_SALE_IDX].links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_CONDOS_FOR_SALE,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Condos For Sale`,
      link: `/${formattedSlug}-real-estate/condos`,
    });

    // "Popular Nearby Cities" (Homes for Sale)
    footerDataTemplate[NEARBY_CITIES_IDX].links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_HOMES_FOR_SALE,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Homes for Sale`,
      link: `/${formattedSlug}-real-estate`,
    });

    // "Recently sold"
    footerDataTemplate[RECENTLY_SOLD_IDX].links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_RECENTLY_SOLD_HOMES_IN,
      labelParams: { city: formattedCityName },
      label: `Recently Sold Homes in ${formattedCityName}`,
      link: `/${formattedSlug}-real-estate/sold`,
    });
  });

  // 4) Add popular neighbourhoods (only if not sold)
  const popularNeighbourhoods = await getPopularNeighbourhoods(city, city_slug, province);
  if (!isSold) {
    // Only keep the "Nearby Neighbourhoods" section in the normal (non-sold) scenario
    if (popularNeighbourhoods?.length > 0) {
      footerDataTemplate[NEARBY_NEIGHBOURHOODS_IDX].links = popularNeighbourhoods.map((item: { label: string; link: string }) => {
        return {
          ...item,
          label: capitalizeWords(item.label),
        };
      });
    } else {
      footerDataTemplate.splice(NEARBY_NEIGHBOURHOODS_IDX, 1);
    }
  }

  // 5) If isSold is false, return the full standard data
  if (!isSold) {
    return footerDataTemplate;
  }

  // ------------------------------------------------------------------
  // If isSold = true, we override the standard sections and build
  // the final data array with only:
  //   a) "Active Listing in Nearby Cities" (replace "Popular Nearby Cities")
  //   b) "Active Rentals near [city]"      (replace "For Rent near [city]")
  //   c) NEW "Recently sold near [city]"
  // ------------------------------------------------------------------

  // 5a) Update translation keys for the two sections we want to keep
  footerDataTemplate[NEARBY_CITIES_IDX].titleKey = LOCALE_INTERNAL_LINKS_PATHS.FOOTER_ACTIVE_LISTING_IN_NEARBY_CITIES;
  footerDataTemplate[NEARBY_CITIES_IDX].title = 'Active Listing in Nearby Cities';
  
  footerDataTemplate[HOMES_FOR_RENT_IDX].titleKey = LOCALE_INTERNAL_LINKS_PATHS.FOOTER_ACTIVE_RENTALS_NEAR;
  footerDataTemplate[HOMES_FOR_RENT_IDX].titleParams = { area: city };
  footerDataTemplate[HOMES_FOR_RENT_IDX].title = `Active Rentals near ${city}`;

  // 5b) Keep only those two
  const finalFooterData: LocalizedLinkData[] = [
    footerDataTemplate[NEARBY_CITIES_IDX], // index 3
    footerDataTemplate[HOMES_FOR_RENT_IDX], // index 6
  ];

  // 5c) Create a NEW "Recently sold near [city]" section
  const recentlySoldSection = createLocalizedLinkData(
    LOCALE_INTERNAL_LINKS_PATHS.FOOTER_RECENTLY_SOLD_NEAR,
    { area: city },
    `Recently sold near ${city}`
  );

  nearbyCityMap.slice(0, 10).forEach((currentValue: NearbyCity) => {
    const formattedSlug = replaceFrenchAccents(currentValue.slug);
    const formattedCityName = standardizedCityName(currentValue.city);

    recentlySoldSection.links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_RECENTLY_SOLD_HOMES_IN,
      labelParams: { city: formattedCityName },
      label: `Recently Sold Homes in ${formattedCityName}`,
      link: `/${formattedSlug}-real-estate/sold`,
    });
  });

  finalFooterData.push(recentlySoldSection);

  return finalFooterData;
}

/**
 * Generates localized neighbourhood footer data with translation keys.
 * This is the localized version of generateNeighbourhoodFooterData() that returns
 * LocalizedLinkData with translation keys instead of hardcoded strings.
 * 
 * @param city - City name
 * @param city_slug - City slug for URLs
 * @param neighbourhood - Neighbourhood name
 * @param neighbourhood_slug - Neighbourhood slug for URLs
 * @param province - Province/state key
 * @param latitude - Latitude for nearby search
 * @param longitude - Longitude for nearby search
 * @param tenant - Tenant configuration
 * @param useLegacySearchFilter - Whether to use legacy search filter
 * @returns Promise<LocalizedLinkData[]> - Array of localized link data with translation keys
 */
//FIXME: This function uses the tenant.theme.id as the tenant ID. We need to use the tenant.id instead.
export async function generateLocalizedNeighbourhoodFooterData(
  city: string,
  city_slug: string,
  neighbourhood: string,
  neighbourhood_slug: string,
  province: keyof typeof ProvinceAndState,
  latitude: number,
  longitude: number,
  tenant: TenantTypes,
  useLegacySearchFilter = true
): Promise<LocalizedLinkData[]> {
  const nearbyCityMap = await getSurroundingCities(ProvinceAndState[province]) || [];
  const footerDataTemplate: LocalizedLinkData[] = createLocalizedFooterDataTemplate(city);
  const searchApi = SearchApi.create(getGoSearchHost(isServerSide()), useLegacySearchFilter);
  
  footerDataTemplate[PROPERTY_TYPES_IDX].links = createLocalizedPropertyTypesLinkData({ 
    area: neighbourhood, 
    slug: neighbourhood_slug, 
  });
  footerDataTemplate[RECENT_LISTINGS_IDX].links = await getRecentListings(province, city, neighbourhood, searchApi, tenant);

  const footerData = nearbyCityMap.reduce((previousValue: LocalizedLinkData[], currentValue: NearbyCity) => {
    const formattedCityName = standardizedCityName(currentValue.city);
    
    previousValue?.[HOUSES_FOR_SALE_IDX]?.links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_HOUSES_FOR_SALE,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Houses for Sale`,
      link: `/${currentValue.slug}-real-estate/houses`,
    });
    
    previousValue?.[HOMES_FOR_RENT_IDX]?.links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_HOUSES_FOR_RENT,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Houses for Rent`,
      link: `/${currentValue.slug}-real-estate/filter?rental=true&townhouse=false&condo=false`,
    });
    
    previousValue?.[CONDOS_FOR_SALE_IDX]?.links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_CONDOS_FOR_SALE,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Condos For Sale`,
      link: `/${currentValue.slug}-real-estate/condos`,
    });
    
    previousValue?.[NEARBY_CITIES_IDX]?.links.push({
      labelKey: LOCALE_INTERNAL_LINKS_PATHS.FOOTER_CITY_HOMES_FOR_SALE,
      labelParams: { city: formattedCityName },
      label: `${formattedCityName} Homes for Sale`,
      link: `/${currentValue.slug}-real-estate`,
    });
    
    return previousValue;
  }, footerDataTemplate);

  // TODO: replace with go-search func & remove service
  const nearbyNeighbourhood = await getNearbyNeighbourhoods(city_slug, latitude, longitude);
  if (nearbyNeighbourhood?.length > 0) {
    footerDataTemplate[NEARBY_NEIGHBOURHOODS_IDX].links = nearbyNeighbourhood;
  } else {
    footerDataTemplate.splice(NEARBY_NEIGHBOURHOODS_IDX, 1);
  }

  return footerData;
}